import React, { useEffect, useRef, useState } from 'react';
import { fetchJson } from '../lib/http';

interface Props {
  open: boolean;
  onClose: () => void;
  initial?: Partial<{
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    billing_addr_line1: string | null;
    billing_addr_city: string | null;
    billing_addr_state: string | null;
    billing_addr_zip: string | null;
  }>;
}

export function UpdateContactModal({ open, onClose, initial }: Props) {
  const [form, setForm] = useState({
    first_name: initial?.first_name || '',
    last_name: initial?.last_name || '',
    phone: initial?.phone || '',
    billing_addr_line1: initial?.billing_addr_line1 || '',
    billing_addr_city: initial?.billing_addr_city || '',
    billing_addr_state: initial?.billing_addr_state || '',
    billing_addr_zip: initial?.billing_addr_zip || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // Autofill from initial profile when modal opens or when initial changes while open
  useEffect(() => {
    if (!open) return;
    setForm({
      first_name: initial?.first_name || '',
      last_name: initial?.last_name || '',
      phone: initial?.phone || '',
      billing_addr_line1: initial?.billing_addr_line1 || '',
      billing_addr_city: initial?.billing_addr_city || '',
      billing_addr_state: initial?.billing_addr_state || '',
      billing_addr_zip: initial?.billing_addr_zip || '',
    });
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    const prevActive = document.activeElement as HTMLElement | null;
    firstFieldRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const onFocus = (e: FocusEvent) => {
      if (!dialogRef.current) return;
      if (dialogRef.current.contains(e.target as Node)) return;
      e.stopPropagation();
      firstFieldRef.current?.focus();
    };
    document.addEventListener('focus', onFocus, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focus', onFocus, true);
      prevActive?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const atLeastOne = Object.values(form).some(v => typeof v === 'string' && v.trim().length > 0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        const s = String(v || '').trim();
        if (s) payload[k] = s;
      }
      await fetchJson('/portal/customer-update-requests', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      setSuccess('Thank you for submitting, your request has been sent and is pending business approval!');
    } catch (e: any) {
      setError(e?.message || 'Failed to submit update request');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="update-contact-title" aria-describedby="update-contact-desc" className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div ref={dialogRef} className="bg-white rounded-2xl shadow-xl w-full max-w-xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 id="update-contact-title" className="text-lg font-bold">Update your contact information</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra" aria-label="Close">✕</button>
        </div>
        <form onSubmit={onSubmit}>
          <p id="update-contact-desc" className="sr-only">Provide any fields you want to update and submit for business approval.</p>
          <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700">First name</label>
              <input ref={firstFieldRef} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">Last name</label>
              <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700">Billing address</label>
              <input value={form.billing_addr_line1} onChange={e => setForm({ ...form, billing_addr_line1: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">City</label>
              <input value={form.billing_addr_city} onChange={e => setForm({ ...form, billing_addr_city: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">State</label>
              <input value={form.billing_addr_state} onChange={e => setForm({ ...form, billing_addr_state: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">ZIP</label>
              <input value={form.billing_addr_zip} onChange={e => setForm({ ...form, billing_addr_zip: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sedra" />
            </div>
          </div>
          <div className="px-6 pb-4 pt-2">
            {error && <div className="mb-2 text-sm text-red-700">{error}</div>}
            {success && <div className="mb-2 text-sm text-green-700">{success}</div>}
            <div className="flex items-center justify-end gap-3">
              <button type="button" onClick={onClose} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2">Cancel</button>
              <button type="submit" disabled={!atLeastOne || submitting} className="rounded-xl bg-sedra text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2">
                {submitting ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UpdateContactModal;


